import React, { useState, useRef, useEffect } from "react";
import "./NavBar.css";
import { Link, useNavigate } from "react-router-dom";
import { FaCartArrowDown } from "react-icons/fa";
import { CgProfile } from "react-icons/cg";
import Profile from "../Profile/Profile";
import { Logo2 } from "../../Assests/Assests";
import { TbListCheck } from "react-icons/tb";
import MarqueeBanner from "./MarqueeBanner";
import FlyToCart, { computeFlight } from "../FlyToCart/FlyToCart";

/* The clone spends this long in the air — must match the fly-to-cart
   animation duration in FlyToCart.css. The cart icon reacts slightly BEFORE
   the clone lands so the two overlap and read as one impact rather than two
   animations in a row. */
const LAND_DELAY_MS = 520;

/* Indicative swatches for the colour groups in the Collections menu — a
   muted clay, a slate and a stone. They are a hint at what is behind the
   link, not brand colours, and they are deliberately desaturated so they
   read as considered against the forest ground rather than decorative.

   Keyed by the group's own value. A group that is not in this map gets no
   swatch, which is the correct answer for a colour we cannot name: a wrong
   swatch is worse than none. */
const COLOR_SWATCHES = {
  warms:    "#BE8A62",
  cools:    "#7A93A0",
  neutrals: "#B9B2A3",
};

const COLOR_GROUPS = [
  { label: "Warms", value: "warms" },
  { label: "Cools", value: "cools" },
  { label: "Neutrals", value: "neutrals" },
];


const NavBar = () => {
  const [isProfileVisible, setIsProfileVisible] = useState(false);
  const [cartPulse, setCartPulse] = useState(null); // { n, delay } — bump to replay
  const [flight, setFlight] = useState(null);       // the clone in the air, or null
  const cartBtnRef = useRef(null);
  const pulseCount = useRef(0);
  const [categories, setCategories] = useState([]);
  const [showCollections, setShowCollections] = useState(false);
  const [collectionsTab, setCollectionsTab] = useState("colors");
  const collectionsRef = useRef(null);
  const profileRef = useRef(null);
  const hideTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("https://admin.riymay.com/api/v1/admin/get/all/main/product-categories")
      .then(r => r.json())
      .then(d => setCategories(d.response || d.mainProductCategories || []))
      .catch(() => {});
  }, []);

  // The product page fires this after a successful add. Nothing in the navbar
  // showed that anything had happened.
  useEffect(() => {
    // Bumping a counter used as a React key remounts the wrapper, and a fresh
    // element restarts its CSS animation. No class toggling, no waiting a
    // frame, no timers — replaying a second add is free and exact.
    //
    // The previous version removed the class and re-added it via
    // requestAnimationFrame with a timer fallback. Neither fires in throttled
    // or embedded contexts, and the roll simply never played. Same failure the
    // confirmation panel had.
    //
    // The event now carries an optional { image, rect } so the page that did
    // the adding can hand us the photo to throw. Everything about the flight
    // is measured here, synchronously, while the click is still on the stack.
    const onCartChanged = (e) => {
      const detail = (e && e.detail) || {};
      const next = pulseCount.current + 1;
      pulseCount.current = next;

      const launched = computeFlight(
        detail.rect,
        cartBtnRef.current,
        detail.image,
        next
      );

      // Null is a normal outcome (no photo, photo scrolled out of view, cart
      // not measurable). The icon then reacts immediately instead of waiting
      // for a flight that is not happening.
      setFlight(launched);
      setCartPulse({ n: next, delay: launched ? LAND_DELAY_MS : 0 });
    };
    window.addEventListener("riymay:cart-changed", onCartChanged);
    return () => window.removeEventListener("riymay:cart-changed", onCartChanged);
  }, []);

  const toggleProfile = (event) => {
    event.stopPropagation(); // Prevent click from reaching document
    setIsProfileVisible((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (profileRef.current && !profileRef.current.contains(event.target)) {
      setIsProfileVisible(false);
    }
  };

  useEffect(() => {
    if (isProfileVisible) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isProfileVisible]);

  /* On a pointer device the menu closes on mouseleave. On touch there is no
     mouseleave, so a tap anywhere else has to close it. */
  useEffect(() => {
    if (!showCollections) return;
    const onDocClick = (event) => {
      if (collectionsRef.current && !collectionsRef.current.contains(event.target)) {
        setShowCollections(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showCollections]);

  const handleProtectedNavigation = (path) => {
    const token = localStorage.getItem("userToken");
    if (!token) {
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  return (
    <div className="navbar-container">
      <MarqueeBanner />
      <nav className="navbar">
        <div className="navbar-section">
          <nav className="navbar-links">
            <div
              className="navbar-link navbar-link--dropdown"
              ref={collectionsRef}
              onMouseEnter={() => {
                if (hideTimeout.current) clearTimeout(hideTimeout.current);
                setShowCollections(true);
              }}
              onMouseLeave={() => {
                hideTimeout.current = setTimeout(() => setShowCollections(false), 200);
              }}
              onClick={(e) => {
                /* A phone never fires a real hover, so on touch this
                   dropdown could only ever be opened with a mouse — the
                   menu was unreachable there. A tap now toggles it.
                   Guarded to coarse pointers so a desktop click, which
                   arrives while hover has already opened the panel, does
                   not immediately shut it again. Clicks that came from
                   inside the panel belong to the links in it. */
                if (e.target.closest && e.target.closest(".collections-megamenu")) return;
                if (window.matchMedia && window.matchMedia("(hover: none)").matches) {
                  /* Stops this same click reaching the close-on-outside-tap
                     listener that the open state is about to install. */
                  e.stopPropagation();
                  setShowCollections((v) => !v);
                }
              }}
            >
              <span className="navbar-link-label">Collections</span>
              {showCollections && (
                <div className="collections-megamenu">
                  {/* Horizontal tabs */}
                  <div className="megamenu-tabs">
                    <button
                      className={`megamenu-tab ${collectionsTab === "colors" ? "megamenu-tab-active" : ""}`}
                      onClick={() => setCollectionsTab("colors")}
                    >
                      Colors
                    </button>
                    <button
                      className={`megamenu-tab ${collectionsTab === "categories" ? "megamenu-tab-active" : ""}`}
                      onClick={() => setCollectionsTab("categories")}
                    >
                      Categories
                    </button>
                  </div>

                  {/* Items below the tabs */}
                  <div className="megamenu-items">
                    {collectionsTab === "colors" && (
                      <>
                        {COLOR_GROUPS.map(c => {
                          const swatch = COLOR_SWATCHES[c.value];
                          return (
                            <Link
                              key={c.value}
                              to={`/newproduct?colorGroup=${c.value}`}
                              className="megamenu-item"
                              onClick={() => setShowCollections(false)}
                            >
                              {c.label}
                              {/* Decorative only — the label already says
                                  which group this is, so it is hidden from
                                  screen readers rather than announced as an
                                  unnamed blank. */}
                              {swatch && (
                                <span
                                  className="megamenu-swatch"
                                  style={{ backgroundColor: swatch }}
                                  aria-hidden="true"
                                />
                              )}
                            </Link>
                          );
                        })}
                      </>
                    )}
                    {collectionsTab === "categories" && (
                      <>
                        {categories.map(cat => (
                          <Link
                            key={cat._id}
                            to={`/newproduct?category=${encodeURIComponent(cat.name)}`}
                            className="megamenu-item"
                            onClick={() => setShowCollections(false)}
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link to="/newproduct" className="navbar-link">All Products</Link>
            <Link to="/spectacle" className="navbar-link">Spectacle</Link>
            <Link to="/About" className="navbar-link">About</Link>
          </nav>
        </div>
        <div className="navbar-section">
          <Link to="/">
            <img src={Logo2} alt="Main Logo" className="main-logo" />
          </Link>
        </div>
        <div className="navbar-section">
          <div className="navbar-icons">
            <button className="icon-button nav-tip" data-tip="Orders" onClick={() => handleProtectedNavigation("/order-history")}>
              <TbListCheck />
            </button>
            <button
              ref={cartBtnRef}
              className="icon-button nav-tip cart-button"
              data-tip="Cart"
              onClick={() => handleProtectedNavigation("/cart")}
            >
              {/* The key is the pulse counter: a new key means a new element,
                  and a new element restarts its CSS animation from the top.
                  The delay is a CSS custom property, not a setTimeout, so the
                  reaction is still guaranteed to run in a throttled tab. */}
              <span
                key={cartPulse ? cartPulse.n : 0}
                className={cartPulse ? "cart-anim" : ""}
                style={cartPulse ? { "--cart-land-delay": `${cartPulse.delay}ms` } : undefined}
              >
                <FaCartArrowDown />
              </span>
            </button>
            <button className="icon-button nav-tip" data-tip="Profile" onClick={toggleProfile}>
              <CgProfile />
            </button>
          </div>
        </div>
      </nav>

{isProfileVisible && (
        <div className="user-profilecard" ref={profileRef}>
          <Profile />
        </div>
      )}

      {/* Position: fixed, so it does not matter where in the tree it sits —
          only that it is inside the component that owns the cart target. */}
      <FlyToCart flight={flight} />
    </div>
  );
};

export default NavBar;
